import React, { Component } from 'react';
import axios from "axios";
import Layout from "./Components/Layout";
import { Navigate } from 'react-router-dom';
import { toast } from "react-toastify";
import SupplierCsvBoxComponent from "./Components/SupplierCsvBoxComponent";

export class ImportSuppliers extends Component {
    constructor(props) {
        super(props);
        this.state = {
            redirect: false,
            supplierDropdown: [],
            selectedOption: 0,
            selectedSupplierName: '',
            loading: false,
            checkingRecords: false,
            hasExistingRecords: false,
            recordsChecked: false,
            downloadComplete: false,
            selectedFile: null,
            fileError: '',
            uploadingFile: false,
            confirmationEmailUrl: null,
            confirmationEmailS3Key: null,
            uploadMessage: '',
            uploadMessageType: ''
        };
        this.fileInputRef = React.createRef();
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
        // Fetch suppliers dropdown data
        axios.get(`get-suppliers-dropdown/0`)
            .then(response => {
                if (this._isMounted) {
                    const suppliers = response.data.suppliers;
                    this.setState({ supplierDropdown: suppliers });
                }
            }).catch(error => {
                console.error('There was an error fetching suppliers!', error);
                toast.error('Failed to load suppliers');
            });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    handleSelectChange = async (event) => {
        const newSelectedOption = event.target.value;
        const selectedSupplier = this.state.supplierDropdown.find(supplier => supplier.id == newSelectedOption);

        let newSupplierName = '';
        if (selectedSupplier) {
            newSupplierName = selectedSupplier.company;
        }

        this.setState({
            selectedOption: newSelectedOption,
            selectedSupplierName: newSupplierName,
            recordsChecked: false,
            hasExistingRecords: false,
            downloadComplete: false,
            selectedFile: null,
            fileError: '',
            confirmationEmailUrl: null,
            confirmationEmailS3Key: null,
            uploadMessage: '',
            uploadMessageType: ''
        });

        // Reset file input
        if (this.fileInputRef.current) {
            this.fileInputRef.current.value = '';
        }

        // Check if supplier has existing records
        if (newSelectedOption > 0) {
            this.checkSupplierRecords(newSelectedOption);
        }
    }

    checkSupplierRecords = async (supplierId) => {
        this.setState({ checkingRecords: true });
        
        try {
            // Use the same API endpoint as SuppliersData.js
            const dataObj = {
                search: '',
                order: 'desc',
                page: 1,
                filterByCompany: parseInt(supplierId)
            };
            
            const response = await axios.put(`get-suppliers`, dataObj);
            const hasRecords = response.data.records && response.data.records.length > 0;
            
            this.setState({ 
                hasExistingRecords: hasRecords,
                recordsChecked: true,
                checkingRecords: false
            });
        } catch (error) {
            console.error('Error checking supplier records:', error);
            toast.error('Failed to check supplier records');
            this.setState({ 
                checkingRecords: false,
                recordsChecked: true 
            });
        }
    }

    handleDownload = async (e) => {
        e.preventDefault();
        
        if (this.state.selectedOption === 0 || this.state.selectedOption === '0') {
            toast.error('Please select a supplier first');
            return;
        }

        this.setState({ loading: true });
        
        try {
            // Call the new API endpoint to export supplier data as XLSX
            const response = await axios.get(
                `export-supplier-data/${this.state.selectedOption}`,
                {
                    responseType: 'blob' // Important for file download
                }
            );
            
            // Create a blob URL and trigger download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${this.state.selectedSupplierName}_currentQuotes.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Download completed successfully');
            this.setState({ downloadComplete: true });
        } catch (error) {
            console.error('Error downloading supplier data:', error);
            if (error.response && error.response.status === 404) {
                toast.error('No records found for this supplier');
            } else {
                toast.error('Failed to download supplier data');
            }
        } finally {
            this.setState({ loading: false });
        }
    }


    handleFileChange = (event) => {
        const file = event.target.files[0];
        this.setState({ fileError: '', uploadMessage: '', uploadMessageType: '' });

        if (file) {
            // Validate file type
            const allowedTypes = ['application/pdf', 'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'text/plain', 'image/jpeg', 'image/png', 'image/gif'];

            if (!allowedTypes.includes(file.type)) {
                this.setState({ 
                    fileError: 'Please select a valid file type (PDF, DOC, DOCX, TXT, JPG, PNG, GIF)',
                    selectedFile: null
                });
                return;
            }

            // Validate file size (5MB limit)
            const maxSize = 5 * 1024 * 1024; // 5MB in bytes
            if (file.size > maxSize) {
                this.setState({ 
                    fileError: 'File size must be less than 5MB',
                    selectedFile: null
                });
                return;
            }

            this.setState({ selectedFile: file });
        } else {
            this.setState({ selectedFile: null });
        }
    }

    handleFileUpload = (e) => {
        // Prevent default form submission
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Validation
        if (!this.state.selectedFile) {
            this.setState({ 
                uploadMessage: 'Please select a confirmation email file',
                uploadMessageType: 'error'
            });
            return;
        }

        if (this.state.selectedOption === 0 || this.state.selectedOption === '0') {
            this.setState({ 
                uploadMessage: 'Please select a supplier first',
                uploadMessageType: 'error'
            });
            return;
        }

        // Set uploading state
        this.setState({ 
            uploadingFile: true, 
            uploadMessage: '', 
            uploadMessageType: '' 
        });


        const formData = new FormData();
        formData.append('confirmationEmail', this.state.selectedFile);
        formData.append('SupplierId', this.state.selectedOption);
        formData.append('SupplierName', this.state.selectedSupplierName);

        axios.post('upload-confirmation-email', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        .then(response => {
            // Check if component is still mounted before updating state
            if (this._isMounted) {
                if (response && response.data && response.data.success) {
                    this.setState({
                        confirmationEmailUrl: response.data.file_url,
                        confirmationEmailS3Key: response.data.s3_key,
                        uploadingFile: false,
                        uploadMessage: 'File uploaded successfully',
                        uploadMessageType: 'success'
                    });
                    toast.success('File uploaded successfully');
                } else {
                    const errorMsg = response?.data?.error || 'Failed to upload file';
                    this.setState({ 
                        uploadingFile: false,
                        uploadMessage: errorMsg,
                        uploadMessageType: 'error'
                    });
                    toast.error(errorMsg);
                }
            }
        })
        .catch(error => {
            console.error('Error uploading file:', error);
            // Check if component is still mounted before updating state
            if (this._isMounted) {
                const errorMessage = error?.response?.data?.error || 'Error uploading file. Please try again.';
                this.setState({ 
                    uploadingFile: false,
                    uploadMessage: errorMessage,
                    uploadMessageType: 'error'
                });
                toast.error(errorMessage);
            }
        });
    
    }

    handleCancel = () => {
        this.setState({ redirect: true });
    }

    handleDownloadSample = () => {
        const element = document.createElement("a");
        element.href = axios.defaults.baseURL + 'suppliers/download-sample-file';
        element.download = "Supplier-Import-File-Format Template.xlsx";
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }

    handleImportComplete = (result, data) => {
        console.log('Import completed:', result, data);
        // Any additional logic after import completion
    }

    render() {
        if (this.state.redirect) {
            return <Navigate to="/suppliers-data" />;
        }

        return (
            <Layout>
                <div style={{ height: 'calc(100vh - 70px)', overflow: 'auto', padding: '20px 0px' }}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-lg-10">
                                <div className="card" style={{ border: '1px solid #e5e7eb' }}>
                                    <div className="card-body">

                                        <div className="mb-4">
                                            <label htmlFor="SupplierName" className="form-label">
                                                Select Supplier <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                className="form-control"
                                                style={{ height: '45px' }}
                                                value={this.state.selectedOption}
                                                onChange={this.handleSelectChange}
                                                required
                                            >
                                                <option value={0}>Select a supplier...</option>
                                                {this.state.supplierDropdown.map((option) => (
                                                    <option key={option.id} value={option.id}>
                                                        {option.company}
                                                    </option>
                                                ))}
                                            </select>
                                            {this.state.selectedOption === 0 && (
                                                <div className="text-danger mt-2" style={{ fontSize: '14px' }}>
                                                    <i className="ti-alert-circle me-1"></i>
                                                    Please select a supplier
                                                </div>
                                            )}
                                        </div>


                                        {this.state.checkingRecords && (
                                            <div className="mb-4">
                                                <div className="text-center p-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden"></span>
                                                    </div>
                                                    <p className="mt-2 mb-0">Checking for existing records...</p>
                                                </div>
                                            </div>
                                        )}

                                        {this.state.recordsChecked && this.state.selectedOption > 0 && (
                                            <div className="mb-4">
                                                {this.state.hasExistingRecords ? (
                                                    <div className="alert alert-warning" style={{ border: '1px solid #ffc107' }}>
                                                        <h6 className="alert-heading">
                                                            <i className="ti-alert-circle me-2"></i>Existing Records Found
                                                        </h6>
                                                        <p className="mb-3">
                                                            This supplier already has existing records in the system. 
                                                            Please download the current data first before importing new records.
                                                        </p>
                                                        {this.state.downloadComplete && (
                                                            <div className="alert alert-success mb-0">
                                                                <i className="ti-check me-2"></i>Download completed! You can now proceed with importing new data.
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="alert alert-success" style={{ border: '1px solid #28a745' }}>
                                                        <h6 className="alert-heading">
                                                            <i className="ti-check-box me-2"></i>No Existing Records
                                                        </h6>
                                                        <p className="mb-0">
                                                            This supplier has no existing records. You can proceed directly with importing data.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Import Section - Only show when ready to import */}
                                        {this.state.recordsChecked && this.state.selectedOption > 0 && 
                                         ((!this.state.hasExistingRecords) || (this.state.hasExistingRecords && this.state.downloadComplete)) && (
                                            <div className="mb-4">
                                                <div className="card" style={{ border: '1px solid #28a745' }}>
                                                    <div className="card-header bg-success text-white">
                                                        <h6 className="mb-0">
                                                            <i className="ti-upload me-2"></i>Import Data
                                                        </h6>
                                                    </div>
                                                    <div className="card-body">
                                                            
                                                        {/* File Upload Section */}
                                                        <div className="mb-4" key={`upload-section-${this.state.selectedOption}`}>
                                                            <label htmlFor="confirmationEmail" className="form-label">
                                                                Confirmation Email <span className="text-danger">*</span>
                                                            </label>
                                                            <div className="input-group">
                                                                <span className="input-group-text">
                                                                    <i className="ti-file"></i>
                                                                </span>
                                                                <input
                                                                    ref={this.fileInputRef}
                                                                    type="file"
                                                                    className="form-control"
                                                                    id="confirmationEmail"
                                                                    name="confirmationEmail"
                                                                    onChange={this.handleFileChange}
                                                                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                                                                    disabled={this.state.uploadingFile || !!this.state.confirmationEmailUrl}
                                                                    style={{ height: '45px' }}
                                                                />
                                                            </div>
                                                            <small className="text-muted">
                                                                Required - PDF, DOC, DOCX, TXT, JPG, PNG, GIF - Max 5MB
                                                            </small>
                                                                
                                                                {/* File error message - using display style */}
                                                                <div className="text-danger mt-1" style={{
                                                                    fontSize: '14px',
                                                                    display: this.state.fileError ? 'block' : 'none'
                                                                }}>
                                                                    {this.state.fileError}
                                                                </div>
                                                                
                                                                {/* Selected file info - using display style */}
                                                                <div className="text-success mt-1" style={{
                                                                    fontSize: '14px',
                                                                    display: (this.state.selectedFile && !this.state.confirmationEmailUrl) ? 'block' : 'none'
                                                                }}>
                                                                    Selected: {this.state.selectedFile ? `${this.state.selectedFile.name} (${(this.state.selectedFile.size / 1024 / 1024).toFixed(2)} MB)` : ''}
                                                                </div>
                                                                
       
                                                                
                                                            {/* Upload button - only show when file is selected */}
                                                            <button 
                                                                type="button"
                                                                className="btn btn-primary mt-3"
                                                                onClick={(e) => this.handleFileUpload(e)}
                                                                disabled={this.state.uploadingFile}
                                                                style={{
                                                                    display: (this.state.selectedFile && !this.state.confirmationEmailUrl) ? 'inline-block' : 'none',
                                                                    height: '45px',
                                                                    padding: '0 24px'
                                                                }}
                                                            >
                                                                {this.state.uploadingFile ? (
                                                                    <>
                                                                        <i className="ti-upload me-2"></i>Uploading...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="ti-upload me-2"></i>Upload File
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>

                                                        {/* CSVBox section - only render when we have the URLs */}
                                                        {this.state.confirmationEmailUrl && this.state.confirmationEmailS3Key ? (
                                                            <div>
                                                                <h6 className="mb-3">Choose an Excel file before pressing the Import button</h6>
                                                                <button 
                                                                    className="btn btn-link p-0 mb-3" 
                                                                    onClick={this.handleDownloadSample}
                                                                >
                                                                    <i className="ti-download me-2"></i>
                                                                    Download Sample Import File
                                                                </button>

                                                                <SupplierCsvBoxComponent 
                                                                    selectedSupplierId={this.state.selectedOption}
                                                                    selectedSupplierName={this.state.selectedSupplierName}
                                                                    confirmationEmailUrl={this.state.confirmationEmailUrl}
                                                                    confirmationEmailS3Key={this.state.confirmationEmailS3Key}
                                                                    onImportComplete={this.handleImportComplete}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="alert alert-info">
                                                                <i className="ti-info-alt me-2"></i>
                                                                Please upload a confirmation email file before importing data
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="d-flex justify-content-between mt-4">
                                            <button 
                                                type="button" 
                                                className="btn btn-secondary"
                                                onClick={this.handleCancel}
                                                style={{ height: '45px', padding: '0 24px' }}
                                            >
                                                <i className="ti-arrow-left me-2"></i>Back to Suppliers
                                            </button>
                                            
                                            {this.state.recordsChecked && this.state.selectedOption > 0 && 
                                             this.state.hasExistingRecords && !this.state.downloadComplete && (
                                                <button 
                                                    type="button" 
                                                    className="btn btn-primary"
                                                    onClick={this.handleDownload}
                                                    disabled={this.state.loading}
                                                    style={{ height: '45px', padding: '0 24px' }}
                                                >
                                                    {this.state.loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Downloading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="ti-download me-2"></i>Download Existing Data
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }
}

export default ImportSuppliers;